/**
 * Reads the hazardous materials block out of a BOL's notes.
 *
 * A BOL carries only a boolean `isDG`; the shipping description itself lives in
 * the notes text. This is the reading half of the format FMSWeb3 writes — see
 * FMSWeb3 `client/src/utils/hazmat.ts` (writer) and `server/src/utils/hazmat.ts`
 * (the other reader). All three must stay in step.
 *
 * Read-only on purpose. A driver sees what the shipper declared; nothing here
 * edits it, and the app must never present the block as something the driver
 * can change from the cab.
 *
 * Two formats are understood: the current block, and the older one the EDI
 * service wrote, which is still on historical BOLs.
 */

export interface HazmatEntry {
  idNumber: string;
  properShippingName: string;
  hazardClass: string;
  packingGroup: string;
  packages: string;
  quantity: string;
  isRQ: boolean;
}

export interface HazmatDetails {
  entries: HazmatEntry[];
  emergencyPhone: string;
  emergencyContract: string;
  ergGuide: string;
  placards: string;
  certifierName: string;
  certifierTitle: string;
}

const BEGIN = '=== HAZARDOUS MATERIALS (49 CFR 172.200) ===';
const END = '=== END HAZARDOUS MATERIALS ===';

const emptyEntry = (): HazmatEntry => ({
  idNumber: '', properShippingName: '', hazardClass: '',
  packingGroup: '', packages: '', quantity: '', isRQ: false,
});

const emptyDetails = (): HazmatDetails => ({
  entries: [], emergencyPhone: '', emergencyContract: '', ergGuide: '',
  placards: '', certifierName: '', certifierTitle: '',
});

/** One material's description, in the sequence 49 CFR 172.202(a) requires. */
export function basicDescription(entry: HazmatEntry): string {
  return [
    entry.isRQ ? 'RQ' : '',
    entry.idNumber,
    entry.properShippingName,
    entry.hazardClass ? `Class ${entry.hazardClass}` : '',
    entry.packingGroup && entry.packingGroup !== 'None' ? `PG ${entry.packingGroup}` : '',
  ].filter(Boolean).join(', ');
}

/** Null when the notes carry no hazmat block in either format. */
export function parseHazmatNotes(notes: string | null | undefined): HazmatDetails | null {
  if (!notes) return null;
  const start = notes.indexOf(BEGIN);
  if (start === -1) return parseLegacyEdiHazmat(notes);

  const endIdx = notes.indexOf(END, start);
  const body = notes.slice(start + BEGIN.length, endIdx === -1 ? undefined : endIdx);

  const details = emptyDetails();
  let current: HazmatEntry | null = null;

  for (const raw of body.split('\n')) {
    const line = raw.trim();
    if (!line) continue;

    const hm = /^HM\s+\d+:\s*(.+)$/.exec(line);
    if (hm) {
      if (current) details.entries.push(current);
      current = emptyEntry();
      const parts = hm[1].split(',').map(x => x.trim());
      if (parts[0] === 'RQ') { current.isRQ = true; parts.shift(); }
      current.idNumber = parts.shift() ?? '';
      const pg = parts.findIndex(x => /^PG\s/i.test(x));
      if (pg !== -1) current.packingGroup = parts.splice(pg, 1)[0].replace(/^PG\s*/i, '');
      const cls = parts.findIndex(x => /^Class\s/i.test(x));
      if (cls !== -1) current.hazardClass = parts.splice(cls, 1)[0].replace(/^Class\s*/i, '');
      // Whatever is left is the shipping name — it can legitimately contain
      // commas, so it is rejoined rather than taking only the first part.
      current.properShippingName = parts.join(', ');
      continue;
    }

    const kv = /^([A-Za-z][A-Za-z0-9 ()]*?):\s*(.*)$/.exec(line);
    if (!kv) continue;
    const key = kv[1].toLowerCase();
    const value = kv[2];
    if (key === 'packages' && current) current.packages = value;
    else if (key === 'quantity' && current) current.quantity = value;
    else if (key.startsWith('emergency phone')) details.emergencyPhone = value;
    else if (key === 'emergency contract') details.emergencyContract = value;
    else if (key === 'erg guide') details.ergGuide = value;
    else if (key === 'placards') details.placards = value;
    else if (key === 'certified by') {
      const [name, ...title] = value.split(',');
      details.certifierName = name.trim();
      details.certifierTitle = title.join(',').trim();
    }
  }
  if (current) details.entries.push(current);
  return details;
}

/**
 * The older EDI block, still present on historical BOLs:
 *
 *   /////HAZMAT\\\\\
 *   Contact: CHEMTREC | Phone: 8004249300
 *   UN: UN2291 | Class: 6.1 | Name: ... | Packing Group: III
 *   /////HAZMAT\\\\\
 */
function parseLegacyEdiHazmat(notes: string): HazmatDetails | null {
  const marker = notes.indexOf('/////HAZMAT');
  if (marker === -1) return null;
  const bodyStart = notes.indexOf('\n', marker);
  if (bodyStart === -1) return null;
  const closing = notes.indexOf('/////HAZMAT', bodyStart);
  const body = notes.slice(bodyStart, closing === -1 ? undefined : closing);

  const details = emptyDetails();
  for (const raw of body.split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    // "Key: value" pairs joined by " | ", and one material can be split over
    // two lines — contact on one, description on the other.
    const fields = new Map<string, string>();
    for (const part of line.split('|')) {
      const idx = part.indexOf(':');
      if (idx === -1) continue;
      fields.set(part.slice(0, idx).trim().toLowerCase(), part.slice(idx + 1).trim());
    }
    if (fields.has('phone')) details.emergencyPhone = fields.get('phone') ?? '';
    if (fields.has('contact')) details.emergencyContract = fields.get('contact') ?? '';
    if (fields.has('un')) {
      details.entries.push({
        ...emptyEntry(),
        idNumber: fields.get('un') ?? '',
        properShippingName: fields.get('name') ?? '',
        hazardClass: fields.get('class') ?? '',
        packingGroup: fields.get('packing group') ?? '',
      });
    }
  }
  return details.entries.length > 0 || details.emergencyPhone ? details : null;
}

/** The notes with the hazmat block removed, in either format. */
export function stripHazmatNotes(notes: string | null | undefined): string {
  if (!notes) return '';
  const start = notes.indexOf(BEGIN);
  if (start === -1) {
    const open = notes.indexOf('/////HAZMAT');
    if (open === -1) return notes;
    const second = notes.indexOf('/////HAZMAT', open + 11);
    const closeLine = second === -1 ? -1 : notes.indexOf('\n', second);
    const rest = second === -1 || closeLine === -1 ? '' : notes.slice(closeLine);
    return (notes.slice(0, open) + rest).replace(/\n{3,}/g, '\n\n').trim();
  }
  const end = notes.indexOf(END, start);
  if (end === -1) return notes.slice(0, start).trimEnd();
  return (notes.slice(0, start) + notes.slice(end + END.length))
    .replace(/\n{3,}/g, '\n\n').trim();
}
